import React, { lazy, Suspense, useRef } from 'react'
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom'
import styled from 'styled-components'

import NavBar from '@features/NavBar'
import PrivateRoute from './PrivateRoute'
import { ROUTE } from '@enums/route'
import Layout from '../components/Layout'
import { LoadingSpinner } from '@thriveglobal/thrive-ui-lib'
import { Security } from '@okta/okta-react'
import { oktaAuth } from '@api/okta'
import { AuthProvider } from '@contexts/AuthState'
import PrivateEmailRoute from './PrivateEmailRoute'
import { CourseStateProvider } from '@contexts/CourseState'
import { SidenavContainer, SidenavModes } from '@features/Sidenav'
import { SidenavContent } from '@features/Sidenav/SidenavContent'
import { Sidenav } from '@features/Sidenav/Sidenav'

import { withNavitationLinks } from '@features/Navigation'
import { SidenavAppContent } from '@features/SidenavAppContent'
import { withCustomSegmentPageEvent } from '@api/segment/page/with-custom-page-event'
import { SegmentPage } from '@enums/segment'

const Home = lazy(() => import('../pages/Home'))
const SignUp = lazy(() => import('../components/containers/SignUp'))
const SignUpDetails = lazy(
  () => import('../components/containers/SignUpDetails')
)
const SignIn = lazy(() => import('../components/containers/SignIn'))
const SignInPassword = lazy(
  () => import('../components/containers/SignInPassword')
)
const SignCallback = lazy(() => import('../components/features/SignCallback'))
const SignoutCallback = lazy(
  () => import('../components/features/SignoutCallback')
)
const ForgotPasswordEmail = lazy(
  () => import('../components/containers/ForgotPasswordEmail')
)
const ForgotPasswordConfirmation = lazy(
  () => import('../components/containers/ForgotPasswordConfirmation')
)
const Sms = lazy(() => import('../components/containers/Sms'))

const NewHome = lazy(() => import('@pages/NewHome'))
const Course = lazy(() => import('@pages/Course'))

// move the spinner down so it doesn't collide with the navbar
const SpinnerWrapper = styled.div`
  position: relative;
  top: 50px;
`


const AuthRouter: React.FC = () => {
  const sidenavRef = useRef<{ toggle: Function }>()

  const toggleSidenav = () => sidenavRef.current?.toggle()

  const SidenavContentWithNavigationLinks = withNavitationLinks(SidenavAppContent)
  const NavbarWithNavigationLinks = withNavitationLinks(NavBar)

  const NewHomeWithCustomSegmentPage = withCustomSegmentPageEvent(NewHome, { isAuthRule: {
    loggedInEventName: SegmentPage.newHomeLoggedIn,
    loggedOutEventName: SegmentPage.newHome
  }})

  const CourseWithCustomSegmentPage = withCustomSegmentPageEvent(Course, {
    isAuthRule: {
      loggedInEventName: SegmentPage.course,
      loggedOutEventName: SegmentPage.courseLoggedOut
    }
  })

  return (
    <Router>
      <Security oktaAuth={oktaAuth}>
        <AuthProvider>
          <CourseStateProvider>
            <SidenavContainer 
              ref={sidenavRef} 
              mode={SidenavModes.semiSide}
              width="17rem"
            >
              <Sidenav>
                <SidenavContentWithNavigationLinks toggleSidenav={toggleSidenav}/>
              </Sidenav>
              <SidenavContent>
                <NavbarWithNavigationLinks toggleSidenav={toggleSidenav}/>
                <Layout>
                  <Suspense
                    fallback={
                      <SpinnerWrapper>
                        <LoadingSpinner hasDelay />
                      </SpinnerWrapper>
                    }
                  >
                    <Switch>
                      <Route path={ROUTE.newHome} component={NewHomeWithCustomSegmentPage} />
                      <Route path={ROUTE.course} component={CourseWithCustomSegmentPage} />
  
                      <Route path={ROUTE.logIn} component={SignIn} />
                      <Route path={ROUTE.signUp} component={SignUp} />
                      <Route
                        path={ROUTE.forgotPassword}
                        component={ForgotPasswordEmail}
                      />
                      <Route
                        path={ROUTE.forgotPasswordConfirm}
                        component={ForgotPasswordConfirmation}
                      />
                      <Route path={ROUTE.sms} component={Sms} />
                      <Route path={ROUTE.signCallback} component={SignCallback} />
                      <Route
                        path={ROUTE.signoutCallback}
                        component={SignoutCallback}
                      />
                      <PrivateEmailRoute
                        path={ROUTE.password}
                        component={SignInPassword}
                      />
                      <PrivateEmailRoute
                        path={ROUTE.signUpDetails}
                        component={SignUpDetails}
                      />
                      <PrivateRoute path={ROUTE.home} component={Home} />
                    </Switch>
                  </Suspense>
                </Layout>
              </SidenavContent>
            </SidenavContainer>
          </CourseStateProvider>
        </AuthProvider>
      </Security>
    </Router>
  )
}

export default AuthRouter
